'use client'

import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react'
import en from '@/locales/en.json'
import ar from '@/locales/ar.json'
import { interpolate } from '@/lib/i18n/interpolate'
import { withCounts } from '@/lib/groups/i18n'

export type Locale = 'en' | 'ar'
export type Direction = 'ltr' | 'rtl'

type TranslationValue = string | string[] | Record<string, unknown>
type Translations = Record<string, unknown>

/**
 * Translations are sourced exclusively from the bundled JSON files
 * (`src/locales/{en,ar}.json`). These files are the single source of truth —
 * version-controlled, reviewed in PRs, and the app's own responsibility.
 *
 * There is intentionally NO database round-trip here: dashboard UI text is not
 * tenant-scoped. Per-tenant booking-page overrides live in `tenant_translations`
 * and are merged server-side by the next-intl loader, never on the dashboard.
 */
const staticTranslations: Record<Locale, Translations> = { en, ar }

interface LanguageContextType {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, params?: Record<string, string | number>) => string
  tArray: (key: string) => string[]
  dir: Direction
  isRTL: boolean
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

const STORAGE_KEY = 'taskyinn-locale'

function getNestedValue(obj: Translations, path: string): TranslationValue | undefined {
  return path.split('.').reduce<unknown>((current, key) => {
    if (current && typeof current === 'object' && key in current) {
      return (current as Record<string, unknown>)[key]
    }
    return undefined
  }, obj) as TranslationValue | undefined
}

export { interpolate }

export function LanguageProvider({
  children,
  initialLocale,
}: {
  children: ReactNode
  /**
   * Optional URL-driven locale. When provided (e.g. on marketing/booking
   * `/[locale]/...` routes), this overrides localStorage/browser detection.
   * The dashboard mounts the provider without this prop and uses the user's
   * stored preference instead.
   */
  initialLocale?: Locale
}) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? 'en')
  const [mounted, setMounted] = useState(false)

  // Initialize locale: URL-driven `initialLocale` wins; otherwise read
  // localStorage and fall back to the browser language preference.
  useEffect(() => {
    if (initialLocale) {
      setLocaleState(initialLocale)
      setMounted(true)
      return
    }
    const stored = localStorage.getItem(STORAGE_KEY) as Locale | null
    if (stored && (stored === 'en' || stored === 'ar')) {
      setLocaleState(stored)
    } else {
      // Check browser language preference
      const browserLang = navigator.language.toLowerCase()
      if (browserLang.startsWith('ar')) {
        setLocaleState('ar')
      }
    }
    setMounted(true)
  }, [initialLocale])

  // Update HTML attributes when locale changes. Also mirror to a cookie so
  // server components (e.g. the calendar's SSR grid) render the right
  // language/direction on the next request — localStorage stays the client's
  // source of truth; a cookie miss just means a brief correction on hydration.
  useEffect(() => {
    if (mounted) {
      document.documentElement.lang = locale
      document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr'
      document.cookie = `app-locale=${locale};path=/;max-age=31536000;samesite=lax`
    }
  }, [locale, mounted])

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale)
    localStorage.setItem(STORAGE_KEY, newLocale)
  }, [])

  const t = useCallback((key: string, params?: Record<string, string | number>): string => {
    const value = getNestedValue(staticTranslations[locale], key)
    if (typeof value === 'string') {
      // `{count|guests}`: the count with its noun in the right plural (lib/groups/i18n withCounts).
      return params ? interpolate(withCounts(locale, value, params), params) : value
    }
    // Fallback to English if the current locale is missing the key
    const fallback = getNestedValue(staticTranslations.en, key)
    if (typeof fallback === 'string') {
      return params ? interpolate(withCounts('en', fallback, params), params) : fallback
    }
    // Return key path if nothing found
    return key
  }, [locale])

  const tArray = useCallback((key: string): string[] => {
    const value = getNestedValue(staticTranslations[locale], key)
    if (Array.isArray(value)) {
      return value as string[]
    }
    const fallback = getNestedValue(staticTranslations.en, key)
    if (Array.isArray(fallback)) {
      return fallback as string[]
    }
    return []
  }, [locale])

  const dir: Direction = locale === 'ar' ? 'rtl' : 'ltr'
  const isRTL = locale === 'ar'

  // Memoize the context value so its identity is stable when no input changed
  // — without this, every provider render produced a new value object and every
  // consumer re-rendered (and any consumer destructuring the object into a hook
  // dep array would re-loop indefinitely).
  const contextValue = useMemo<LanguageContextType>(
    () => ({
      locale: mounted ? locale : 'en',
      setLocale,
      t,
      tArray,
      dir: mounted ? dir : 'ltr',
      isRTL: mounted ? isRTL : false,
    }),
    [mounted, locale, setLocale, t, tArray, dir, isRTL]
  )

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  )
}

/**
 * Module-level singleton fallback for `useLanguage` when invoked outside the
 * `LanguageProvider` (e.g. shared components rendered on marketing/booking
 * pages where only `NextIntlClientProvider` is mounted).
 *
 * `t` and `tArray` here ACTUALLY RESOLVE keys against the bundled English
 * JSON — defense in depth so raw key paths never leak to the UI, even if a
 * component reaches into `useDashboardTranslation` from a tree without the
 * `LanguageProvider`. Components that need URL-locale-aware translation in
 * such trees should use `useTranslations` from `next-intl` directly.
 *
 * Hoisted to module scope so the references are stable across calls.
 */
const FALLBACK_LANGUAGE_CONTEXT: LanguageContextType = {
  locale: 'en',
  setLocale: () => {},
  t: (key: string, params?: Record<string, string | number>) => {
    const value = getNestedValue(staticTranslations.en, key)
    if (typeof value === 'string') {
      return params ? interpolate(withCounts('en', value, params), params) : value
    }
    return key
  },
  tArray: (key: string) => {
    const value = getNestedValue(staticTranslations.en, key)
    return Array.isArray(value) ? (value as string[]) : []
  },
  dir: 'ltr',
  isRTL: false,
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  return context ?? FALLBACK_LANGUAGE_CONTEXT
}

/**
 * Shorthand hook for the translation slice of `useLanguage`.
 *
 * Returns a memoized object so consumers that pass the whole returned value
 * into a dep array (or compare by identity) don't see spurious changes.
 */
export function useTranslation() {
  const { t, tArray, locale, dir, isRTL } = useLanguage()
  return useMemo(
    () => ({ t, tArray, locale, dir, isRTL }),
    [t, tArray, locale, dir, isRTL]
  )
}
